import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, Apple } from "lucide-react";
import { Button } from "@/components/ui/button";

const productMenu = [
  {
    label: "Features",
    items: [
      { name: "Smart Groups", href: "#features" },
      { name: "AI Card Scanner", href: "#features" },
      { name: "Voice Reminders", href: "#voice-reminder" },
      { name: "Global Search", href: "#features" },
    ],
  },
  {
    label: "For whom",
    items: [
      { name: "Business owners", href: "#solutions" },
      { name: "Freelancers & creators", href: "#solutions" },
      { name: "Job seekers", href: "#solutions" },
      { name: "Anyone with a messy phonebook", href: "#solutions" },
    ],
  },
];

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

function GradientLogo({ testid }) {
  return (
    <div className="flex items-center gap-2.5" data-testid={testid}>
      <img
        src="https://customer-assets-lxgj4vgw.emergentagent.net/job_lead-capture-156/artifacts/6n67rx1z_logo.png"
        alt="BizBinder"
        className="w-11 h-11 rounded-[10px] shadow-md shadow-[color:var(--blue)]/20"
      />
      <div className="leading-none">
        <div
          className="font-display font-bold text-[18px] tracking-tight gradient-text"
        >
          BizBinder
        </div>
        <div className="text-[9px] tracking-[0.28em] uppercase text-[color:var(--ink-mute)] mt-1 font-medium">
          Organize · Automate · Grow
        </div>
      </div>
    </div>
  );
}

export { GradientLogo };

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [openProduct, setOpenProduct] = useState(false);
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 12);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="site-header"
    >
      <div
        className={`transition-colors duration-500 ${
          scrolled
            ? "bg-[color:var(--bg)]/80 backdrop-blur-xl border-b border-black/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
          <a
            href="#top"
            data-testid="nav-logo"
            onClick={(e) => { e.preventDefault(); scrollTo("#top"); }}
          >
            <GradientLogo />
          </a>

          <nav className="hidden lg:flex items-center gap-1 text-[13px] text-[color:var(--ink-soft)]">
            <div
              className="relative"
              onMouseEnter={() => setOpenProduct(true)}
              onMouseLeave={() => setOpenProduct(false)}
            >
              <button
                data-testid="nav-product"
                className="px-4 py-2 flex items-center gap-1 hover:text-[color:var(--ink)] transition-colors"
              >
                Product <ChevronDown className="w-3.5 h-3.5" />
              </button>
              <AnimatePresence>
                {openProduct && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[520px] p-6 rounded-2xl bg-white/95 backdrop-blur-xl border border-black/10 shadow-2xl"
                    data-testid="nav-product-dropdown"
                  >
                    <div className="grid grid-cols-2 gap-6">
                      {productMenu.map((col) => (
                        <div key={col.label}>
                          <div className="overline mb-3">{col.label}</div>
                          <ul className="space-y-2">
                            {col.items.map((it) => (
                              <li key={it.name}>
                                <a
                                  href={it.href}
                                  className="block text-[color:var(--ink)] hover:text-[color:var(--blue)] transition-colors text-sm py-1"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    scrollTo(it.href);
                                    setOpenProduct(false);
                                  }}
                                >
                                  {it.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {[
              { label: "About", href: "#about" },
              { label: "Contact", href: "#contact" },
            ].map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
                className="px-4 py-2 hover:text-[color:var(--ink)] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollTo("#download")}
              data-testid="nav-download"
              className="hidden md:flex items-center gap-2 text-[13px] text-[color:var(--ink-soft)] hover:text-[color:var(--ink)] px-3 py-2 transition-colors"
            >
              <Apple className="w-4 h-4" />
              Get the app
            </button>
            <Button
              data-testid="nav-book-demo-btn"
              onClick={() => scrollTo("#waitlist")}
              className="rounded-full h-11 px-5 bg-[color:var(--blue)] hover:bg-[color:var(--blue-2)] text-white font-medium transition-colors border-0"
            >
              Join waitlist
            </Button>
            <button
              className="lg:hidden text-[color:var(--ink)] p-2"
              onClick={() => setMobile((m) => !m)}
              data-testid="nav-mobile-toggle"
              aria-label="Open menu"
            >
              {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobile && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden border-t border-black/10 bg-white/95 backdrop-blur-xl"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-6 flex flex-col gap-3 text-[color:var(--ink)]">
                {[
                  { label: "Product", href: "#features" },
                  { label: "About",   href: "#about" },
                  { label: "Contact", href: "#waitlist" },
                ].map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setMobile(false);
                      // Wait for drawer to close, then scroll (avoids race with layout collapse)
                      setTimeout(() => scrollTo(l.href), 320);
                    }}
                    className="py-3 text-base text-[color:var(--ink)] active:opacity-60"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
